import axios from "axios";

// Detectar si estamos en desarrollo o producción
// process.env.NODE_ENV es 'production' durante npm run build — no depender solo de hostname
// porque react-snap también corre en localhost durante el build
const isDevelopment = process.env.NODE_ENV !== 'production' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.startsWith('192.168')
);

// Seleccionar URL base según el entorno
let baseURL;
if (isDevelopment) {
  baseURL = "http://localhost:8000/api";  // Local
} else {
  baseURL = "https://api.planiaro.com/api"; // Producción
}

// Debug: mostrar la URL que se está usando
if (isDevelopment) {
  console.log(`🔗 API Environment: ${isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'}`);
  console.log(`🔗 API Base URL: ${baseURL}`);
}

const api = axios.create({
  baseURL: baseURL,
});

// Clean invalid tokens on initialization
const cleanInvalidTokens = () => {
  const token = localStorage.getItem("token");
  if (token && (token === 'null' || token === 'undefined' || token.trim() === '')) {
    localStorage.removeItem("token");
  }
};

// Initialize token cleanup
cleanInvalidTokens();

api.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Log solo en desarrollo
  if (isDevelopment) {
    const fullUrl = config.baseURL + config.url;
    console.log(`📡 Request: ${config.method?.toUpperCase()} ${fullUrl}`);
  }
  
  return config;
});

// Response interceptor: if 401, try to refresh the token and retry once
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    
    // Logging detallado solo en desarrollo
    if (isDevelopment) {
      console.error('❌ API Error:', {
        status: error.response?.status,
        message: error.message,
        url: originalRequest?.url,
        data: error.response?.data
      });
    }
    
    // Do not attempt token refresh for auth endpoints (login/register/refresh)
    if (originalRequest && originalRequest.url && 
        (originalRequest.url.includes('/login') || 
         originalRequest.url.includes('/register') || 
         originalRequest.url.includes('/refresh'))) {
      return Promise.reject(error);
    }
    
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Only attempt refresh if we have a valid token stored
      const currentToken = localStorage.getItem('token');
      if (!currentToken || currentToken === 'null' || currentToken === 'undefined' || currentToken.trim() === '') {
        localStorage.removeItem('token');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
        .then(token => {
          originalRequest.headers.Authorization = 'Bearer ' + token;
          return axios(originalRequest);
        })
        .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const lang = (window.location.pathname.split('/')[1]) || 'es';
        const refreshUrl = isDevelopment 
          ? `http://localhost:8000/api/${lang}/refresh`
          : `https://api.planiaro.com/api/${lang}/refresh`;
        const refreshRes = await axios.post(refreshUrl, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const newToken = refreshRes.data.token;
        if (newToken && newToken !== 'null' && newToken !== 'undefined' && newToken.trim() !== '') {
          localStorage.setItem('token', newToken);
          api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
          originalRequest.headers.Authorization = 'Bearer ' + newToken;
          processQueue(null, newToken);
        } else {
          throw new Error('Invalid token received from refresh');
        }
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem('token');
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
